import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import apiService from "../services/api";
import TourHelpButton from "../components/TourHelpButton";
import { useProductTour } from "../onboarding/useProductTour";
import { TOUR_IDS } from "../onboarding/tours";
import "../styles.css";

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(apiService.getUser());
  const displayName = user?.displayName?.trim();
  const showCaptures = Boolean(user) && String(user.organizationId) !== "200";
  const { isTourRunning, startTour } = useProductTour({
    tourId: TOUR_IDS.dashboard,
    userId: user?.id,
    ready: Boolean(user),
  });

  useEffect(() => {
    const unsubscribe = apiService.onAuthChange((profile) => {
      if (!profile) {
        navigate("/auth");
        return;
      }
      if (profile.role !== "EXPERT") {
        navigate("/pending");
        return;
      }
      setUser(profile);
    });
    return unsubscribe;
  }, [navigate]);

  const handleLogout = async () => {
    await apiService.logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <nav className="p-4 sm:p-6 flex flex-wrap gap-4 justify-between items-center bg-black/30 backdrop-blur-sm">
        <div>
          <h1 className="text-2xl font-bold text-white">Digitomy</h1>
          {user && (
            <p className="text-gray-300 text-sm">{user.username}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <TourHelpButton onClick={startTour} isRunning={isTourRunning} />
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            Logout
          </button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-3">
            Welcome back{displayName ? `, ${displayName}` : ""}
          </h2>
          <p className="text-gray-300 text-lg">
            Please select first what you would like to do today
          </p>
        </div>

        <div className={`grid gap-8 mx-auto ${showCaptures ? "md:grid-cols-2 max-w-4xl" : "max-w-xl"}`}>
          {showCaptures && (
            <motion.button
              type="button"
              data-tour="dashboard-captures"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/captures")}
              className="bg-white/10 hover:bg-white/15 backdrop-blur-lg rounded-2xl p-10 border border-white/20 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 flex flex-col items-center"
            >
              <div className="text-5xl mb-4">📷</div>
              <h3 className="text-2xl font-bold text-white mb-3 min-h-[3.5rem] flex items-center">
                View Trap Captured Images
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Browse and download processed mosquito trap images captured by
                your organization, organized chronologically with the most
                recent captures shown first.
              </p>
            </motion.button>
          )}
          <motion.button
            type="button"
            data-tour="dashboard-annotate"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/annotate")}
            className="bg-white/10 hover:bg-white/15 backdrop-blur-lg rounded-2xl p-10 border border-white/20 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 flex flex-col items-center"
          >
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-white mb-3 min-h-[3.5rem] flex items-center">
              Annotate Mosquito Images
            </h3>
            <p className="text-gray-300 leading-relaxed">
              Annotate mosquito specimen images by identifying their genus,
              species, and associated biological characteristics.
            </p>
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
